<template>
    <v-card>
        <v-card-title>
            <div>{{ $t('event.notification-groups') }}</div>
            <v-spacer></v-spacer>
            <v-btn color="primary" @click="initAddNGroup">
                {{$t('add')}}
                <v-icon right>mdi-plus</v-icon>
            </v-btn>
        </v-card-title>
        <v-card-text>
            <v-data-table 
                :headers="nGroupheaders"
                :items="nGroupItems"
                :loading="isNGroupPending"
            >
                <template v-slot:item.actions="{item}">
                    <div class="my-2">
                        <div style="margin: 0 auto; text-align: center;">
                            <v-btn color="primary" class="mx-1"
                                @click="initEditNGroup(item)">
                                {{$t('edit')}}
                                <v-icon right>mdi-pencil</v-icon>
                            </v-btn>
                            <v-btn color="red" dark class="mx-1"
                                @click="deleteNGroup(item)">
                                {{$t('delete')}}
                                <v-icon right>mdi-delete</v-icon>
                            </v-btn>
                        </div>
                        <div style="margin: 8px auto 0 auto; text-align: center;">
                            <v-btn color="green" dark
                                @click="exportGuest(item)">
                                {{$t('Download Guest Info')}}
                                <v-icon right>mdi-download</v-icon>
                            </v-btn>
                        </div>
                    </div>
                </template>
            </v-data-table>
            <v-dialog v-model="editNGroupDialogShown"
                width="600px"
                persistent
                scrollable
            >
                <v-card>
                    <v-card-title>
                        {{$t('event.notification-group')}}
                    </v-card-title>
                    <v-card-text>
                        <v-container>
                            <v-form ref="nGroupForm" v-model="isEditNGroupFormValid">
                                <v-row>
                                    <v-col class="pb-0">
                                        <v-text-field outlined
                                            :label="$t('event_nGroup.name')"
                                            :placeholder="$t('event_nGroup.name')"
                                            v-model="editingNGroup.name"
                                            :rules="nameRules"
                                        ></v-text-field>
                                    </v-col>
                                </v-row>
                                <v-form ref="importForm" lazy-validation v-model="isImportFormValid">
                                    <v-row>
                                        <v-col class="py-0">
                                            <v-file-input
                                                outlined
                                                prepend-icon=""
                                                prepend-inner-icon="$file"
                                                accept=".xlsx, .xls, .csv"
                                                label="Import File"
                                                placeholder="Select..."
                                                :rules="importFileRules"
                                                v-model="excelFileToBeImported">
                                            </v-file-input>
                                        </v-col>
                                    </v-row>
                                    <v-row>
                                        <v-col class="py-0" cols="12" sm="9">
                                            <v-select
                                                v-model="selectedImportField"
                                                :items="eventGuestFields"
                                                item-text="name"
                                                :label="$t('Excel Field for matching imported guests')"
                                                :rules="importFieldRules"
                                                outlined
                                                return-object
                                            ></v-select>
                                        </v-col>
                                        <v-col class="py-2" cols="12" sm="3">
                                            <v-btn class="mb-5 mr-5" color="primary" :disabled="!isImportFormValid" @click="importExcel">
                                                {{$t('import')}}
                                            </v-btn>
                                        </v-col>
                                    </v-row>
                                </v-form>
                                <v-row>
                                    <v-col class="pt-0">
                                        <v-text-field v-model="search"
                                            append-icon="mdi-magnify"
                                            :label="$t('search')"
                                            single-line
                                            hide-details
                                            clearable
                                        ></v-text-field>
                                    </v-col>
                                </v-row>
                                <v-row>
                                    <v-col>
                                        <v-data-table 
                                            :headers="eventGuestHeaders"
                                            :search="search"
                                            :loading="isNGroupEventGuestsPending"
                                            v-model="selectedEventGuests"
                                            :items="nGroupEventGuests"
                                            :no-data-text="$t('No Guests')"
                                            :sort-by="sortBy"
                                            sort-desc
                                            show-select
                                        ></v-data-table>
                                    </v-col>
                                </v-row>
                            </v-form>
                        </v-container>
                    </v-card-text>
                    <v-divider></v-divider>
                    <v-card-actions>
                        <v-btn color="secondary" @click="confirmCancelItemDialogShown = true">{{$t('cancel')}}</v-btn>
                        <v-spacer></v-spacer>
                        <v-btn color="primary" @click="saveNGroup"
                            :disabled="!isEditNGroupFormValid">{{$t('save')}}
                        </v-btn>
                    </v-card-actions>
                </v-card>
            </v-dialog>
            <v-dialog
                v-model="importLogShown"
                max-width="500px"
                persistent
                scrollable
            >
                <v-card>
                    <v-card-title class="justify-center">
                        {{ $t('Import Log') }}
                    </v-card-title>
                    <v-card-text>
                        <v-container>
                            <v-row style="white-space: pre-line;">
                                {{importLogMsg}}
                            </v-row>
                        </v-container>
                    </v-card-text>
                    <v-divider></v-divider>
                    <v-card-actions>
                        <v-spacer></v-spacer>
                        <v-btn color="secondary" @click="importLogShown = false">{{$t('close')}}</v-btn>    
                    </v-card-actions>
                </v-card>
            </v-dialog>
            <PopupAlert
                v-model="confirmCancelItemDialogShown"
                title="Warning"
                message="Do you wish to cancel your amendments?"
                :onYes="cancelEditNGroup"
                :onNo="function(){confirmCancelItemDialogShown=false;}"
            ></PopupAlert>
        </v-card-text>
        <v-overlay z-index="999" :value="overlay">
            <v-progress-circular indeterminate size="64"></v-progress-circular>
        </v-overlay>
    </v-card>
</template>

<script>
    import {computed, onMounted, ref, watch} from "@vue/composition-api";
    import {useFind} from "feathers-vuex";
    import {DateTime} from "luxon";
    import _ from 'lodash';
    import XLSX from 'xlsx';
    import PopupAlert from "../../components/PopupAlert";
    import eConfig from "../../evention-config";
    import axios from 'axios';

    export default {
        name: 'EventNotificationGroupList',
        components:{PopupAlert},
        setup(props, {root}) {
            const {EventGuest, EventNotificationGroup, ActiveEventNotificationGroup} = root.$FeathersVuex.api;

            const overlay = ref(false);
            const eventId = computed(() => root.$route.params.eventId);
            const editNGroupDialogShown = ref(false);
            const importLogShown = ref(false);
            const confirmCancelItemDialogShown = ref(false);

            const dbColMapping = eConfig.ev_guest.dbColMapping;
            const eventGuestFields = ref(Object.keys(dbColMapping).map((key) => {
                return {
                    name: key,
                    value: dbColMapping[key]
                }
            }));

            const nGroupForm = ref(null);
            const isEditNGroupFormValid = ref(false);
            const nameRules = ref([
                value => !!value || 'Required',
            ]);

            // Table Data
            const isNGroupPending = ref(false);
            const nGroupItems = ref([]);
            const fetchNGroup = async function() {
                isNGroupPending.value = true;
                const result = await EventNotificationGroup.find({
                    query: {
                        eventId: eventId.value,
                        $sort: { createdAt: -1 },
                    }
                });
                nGroupItems.value = result.data;
                isNGroupPending.value = false;
            };
            const isEventGuestPending = ref(false);
            const eventGuests = ref([]);
            const fetchEventGuest = async function() {
                isEventGuestPending.value = true;
                const result = await EventGuest.find({
                    query: {
                        eventId: eventId.value,
                        $sort: { createdAt: -1 },
                    }
                });
                eventGuests.value = result.data;
                isEventGuestPending.value = false;
            };
            onMounted(() => {
                fetchNGroup();
                fetchEventGuest();
            });

            // Table Headers
            const nGroupheaders = ref([
                {text: 'Guest Count', value: 'event_guests.length', width: '15%'},
                {text: 'Group Name', value: 'name', width: '55%'},
                {text: '', value: 'actions', sortable: false},
            ]);
            const eventGuestHeaders = ref([
                {text: 'First Name', value: 'ev_firstname'},
                {text: 'Last Name', value: 'ev_lastname'},
                {text: 'Email', value: 'ev_email'},
                {text: 'Agency ID', value: 'ev_agencyid'},
            ]);

            // Add / Edit NGroup
            const editingNGroup = ref(new EventNotificationGroup({eventId: eventId.value}));
            const originalActiveNGroups = ref([]);
            const selectedEventGuests = ref([]);
            const nGroupEventGuests = ref([]);
            const isNGroupEventGuestsPending = ref(false);
            const initAddNGroup = async function () {
                editingNGroup.value = new EventNotificationGroup({eventId: eventId.value});
                cleanUpBeforeInit();
                editNGroupDialogShown.value = true;
                isNGroupEventGuestsPending.value = true;
                if (eventGuests.value.length == 0) {
                    await fetchEventGuest();
                }
                nGroupEventGuests.value = eventGuests.value;
                isNGroupEventGuestsPending.value = false;
            };
            const initEditNGroup = async function (nGroup) {
                editingNGroup.value = _.cloneDeep(nGroup);
                cleanUpBeforeInit();
                editNGroupDialogShown.value = true;
                isNGroupEventGuestsPending.value = true;
                if (eventGuests.value.length == 0) {
                    await fetchEventGuest();
                }
                if (nGroup.event_guests && Array.isArray(nGroup.event_guests) && nGroup.event_guests.length > 0) {
                    const idlist = nGroup.event_guests.map((eg) => eg.id);
                    const promises = await eventGuests.value.map(async (eg) => {
                        if (idlist.includes(eg.id)) {
                            eg.checked = true;
                            await selectedEventGuests.value.push(eg);
                            await originalActiveNGroups.value.push(eg);
                        } else {
                            eg.checked = false;
                        }
                        return eg;
                    });
                    Promise.all(promises).then((values) => {
                        nGroupEventGuests.value = values;
                    });
                }
                isNGroupEventGuestsPending.value = false;
            };
            const cleanUpBeforeInit = function() {
                originalActiveNGroups.value = [];
                selectedEventGuests.value = [];
                selectedImportField.value = null;
                excelFileToBeImported.value = null;
                if (nGroupForm.value) nGroupForm.value.resetValidation();
                if (importForm.value) importForm.value.resetValidation();
            }
            const deleteNGroup = async function (nGroup) {
                if (confirm(root.$t('event_nGroup.confirm_delete'))) {
                    overlay.value = true;
                    await nGroup.remove().then(null, root.$handleFeatherError.bind(root));
                    overlay.value = false;
                    fetchNGroup();
                }
            };
            const saveNGroup = async function () { 
                overlay.value = true;

                const removedEventGuests = _.differenceWith(originalActiveNGroups.value, selectedEventGuests.value);
                const addedEventGuests = _.differenceWith(selectedEventGuests.value, originalActiveNGroups.value);

                await editingNGroup.value.save().then(() => {
                    if (addedEventGuests.length > 0 || removedEventGuests.length > 0) {
                        saveActiveNGroup(editingNGroup.value.id, addedEventGuests, removedEventGuests);
                    } else {
                        afterSaveSuccessful();
                    }
                    editNGroupDialogShown.value = false;
                }, root.$handleFeatherError.bind(root));

                overlay.value = false;
            };
            const saveActiveNGroup = async function(nGroupId, addedEventGuests, removedEventGuests) {
                try {
                    let activeNGroupToBeRemoved = [];
                    if (removedEventGuests.length > 0) {
                        await ActiveEventNotificationGroup.find({
                            query: {
                                notificationGroupId: nGroupId
                            }
                        }).then((result) => {
                            const removedEventGuestsIds = removedEventGuests.map((e) => e.id);
                            activeNGroupToBeRemoved = result.data.filter((obj) => removedEventGuestsIds.includes(obj.eventGuestId));
                        });
                    }

                    const prefixUrl = process.env.VUE_APP_SERVER_ADDRESS;
                    await axios.create({
                        headers: {'Authorization': window.localStorage.getItem('feathers-jwt')}
                    }).post(`${prefixUrl}/batch-update-api/batch-update`, {
                        service: 'active-notification-groups',
                        instances: activeNGroupToBeRemoved,
                        operation: 'delete'
                    });

                    await axios.create({
                        headers: {'Authorization': window.localStorage.getItem('feathers-jwt')}
                    }).post(`${prefixUrl}/batch-update-api/batch-update`, {
                        service: 'active-notification-groups',
                        instances: addedEventGuests.map((e) => {
                            return {notificationGroupId: nGroupId, eventGuestId: e.id}
                        }),
                        operation: 'create'
                    });
                    
                    afterSaveSuccessful();
                } catch (e) {
                    root.$handleFeatherError(e);
                }
            }
            const afterSaveSuccessful = function() {
                EventNotificationGroup.get(editingNGroup.value.id);
                fetchNGroup();
                root.$store.dispatch('showSnackBar', root.$t('event_nGroup.save_successful'));
            }
            const cancelEditNGroup = function(){
                editNGroupDialogShown.value = false;
                confirmCancelItemDialogShown.value = false;
            }

            const sortBy = ref('checked');

            // Import
            const importForm = ref(null);
            const isImportFormValid = ref(false);
            const importFieldRules = ref([
                value => !!value || 'Required'
            ]);
            const importFileRules = ref([
                value => !!value || 'Required'
            ]);

            const selectedImportField = ref(null);
            const excelFileToBeImported = ref(null);
            const importLogMsg = ref('');

            let currentExcelData = null;

            const readXLSX = (file) => {
                let nameSplit = file.name.split('.');
                let format = nameSplit[nameSplit.length - 1];
                if (!['xls', 'xlsx', 'csv'].includes(format)) {
                    return false;
                }
                return new Promise((resolve) => {
                    let reader = new FileReader();
                    reader.readAsBinaryString(file);
                    reader.onload = function (evt) {
                        let data = evt.target.result;
                        var workbook = XLSX.read(data, {type: 'binary'});
                        let excelData = XLSX.utils.sheet_to_json(workbook.Sheets[workbook.SheetNames[0]], {defval: ''});
                        resolve(excelData);
                    }
                });
            }
            const importExcel = async () => {
                overlay.value = true;
                if (!importForm.value.validate())
                    return false;

                const f = excelFileToBeImported.value;

                if (f) {
                    const excelData = await readXLSX(f);
                    const resultDatas = [];

                    for (let rowObj of excelData) {
                        if (!rowObj.hasOwnProperty(selectedImportField.value.name)) {
                            root.$store.dispatch('showSnackBar', root.$t('No corresponding excel field in your Excel'));
                            return false;
                        }
                        if (rowObj[selectedImportField.value.name] == null || rowObj[selectedImportField.value.name] == undefined) {
                            root.$store.dispatch('showSnackBar', root.$t('Invalid value in your Excel'));
                            return false;
                        }
                        resultDatas.push(rowObj[selectedImportField.value.name]);
                    }

                    if (resultDatas && resultDatas.length >= 1) {
                        currentExcelData = excelData;
                    }

                    const egArr = eventGuests.value.filter(eg => resultDatas.includes(eg[selectedImportField.value.value]))

                    selectedEventGuests.value = egArr;

                    importLogMsg.value = '';
                    importLogMsg.value = `Imported ${egArr.length} guest(s).`;

                    const notFoundEventGuests = _.differenceWith(currentExcelData, selectedEventGuests.value, function(a, b) {
                        return a[selectedImportField.value.name] === b[selectedImportField.value.value];
                    });

                    if (notFoundEventGuests.length > 0) {
                        importLogMsg.value += `\n\n${notFoundEventGuests.length} guest(s) cannot be found.\n`;
                        notFoundEventGuests.forEach(eg=>{
                            importLogMsg.value += `\n- ${selectedImportField.value.name}: "${eg[selectedImportField.value.name]}"`;
                        });
                    }

                    overlay.value = false;
                    importLogShown.value = true;
                }
                overlay.value = false;
            };

            const exportGuest = async (item) => {
                if (!Array.isArray(item.event_guests) || item.event_guests.length <= 0) {
                    root.$store.dispatch('showSnackBar', root.$t('event_nGroup.guest_not_found'));
                    return;
                }
                console.log(item.event_guests.length);
                const titleCols = Object.values(dbColMapping);
                const extraCols = [...new Set(item.event_guests.map((row)=>Object.keys(JSON.parse(row.metadata) || {})).flat(1))];

                let excelData = [];
                for (let eventGuest of item.event_guests) {
                    let guestRow = [];
                    for (let titleCol of titleCols) {
                        guestRow.push(eventGuest[titleCol] || '');
                    }
                    if (eventGuest.metadata) {
                        const extra = JSON.parse(eventGuest.metadata) || {};
                        for (let extraCol of extraCols) {
                            guestRow.push(extra[extraCol] || '');
                        }
                    }
                    excelData.push(guestRow);
                }

                excelData.unshift([...Object.keys(dbColMapping), ...extraCols]);
                const ws = XLSX.utils.aoa_to_sheet(excelData);
                const wb = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(wb, ws, event.name);
                XLSX.writeFile(wb, 'notification_group_guest_'+eventId.value+'_'+DateTime.local().toFormat('yyyyMMdd_HHmmss')+'.xlsx');
            }

            return {
                overlay,
                search: ref(''),
                DateTime,

                editNGroupDialogShown,
                confirmCancelItemDialogShown,
                importLogShown,

                eventGuestFields,

                // Form
                nGroupForm,
                isEditNGroupFormValid,
                nameRules,

                importForm,
                isImportFormValid,
                importFileRules,
                importFieldRules,

                nGroupItems,
                isNGroupPending,

                nGroupheaders,
                eventGuestHeaders,

                editingNGroup,
                eventGuests,
                selectedEventGuests,

                initAddNGroup,
                initEditNGroup,
                deleteNGroup,
                saveNGroup,
                cancelEditNGroup,
                isNGroupEventGuestsPending,

                nGroupEventGuests,
                sortBy,
                isEventGuestPending,

                excelFileToBeImported,
                selectedImportField,
                importLogMsg,

                importExcel,
                exportGuest,
            }
        }
    }
</script>
